import { yupResolver } from '@hookform/resolvers/yup';
import pluralize from 'pluralize';
import { memo, useCallback, useId, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { type InferType, object, string } from 'yup';

import { Banner } from '@air/primitive-banner';
import { Button } from '@air/primitive-button';
import { Field, FieldLabel } from '@air/primitive-field';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';
import { type Clip } from '@air/types';

import { type BatchExportClip, useBatchExportImages } from '~/components/ExportImagesModal/hooks/useBatchExportImages';
import { getBestImgixExportSource, isClipExportable } from '~/utils/Imgix';

const MAX_BATCH_EXPORT_CLIPS = 20;

const SCALE_OPTIONS = [
  { label: '1x (Original)', value: '1' },
  { label: '.75x', value: '0.75' },
  { label: '.5x', value: '0.5' },
  { label: '.25x', value: '0.25' },
] as const;

const FORMAT_OPTIONS = [
  { label: 'JPG', value: 'jpg' },
  { label: 'PNG', value: 'png' },
  { label: 'WEBP', value: 'webp' },
] as const;

export const BatchExportImagesFormValidationSchema = object({
  format: string().required(),
  scale: string().required(),
  suffix: string().optional().default(''),
});

export type BatchExportImagesFormInput = InferType<typeof BatchExportImagesFormValidationSchema>;

export type BatchExportImagesModalProps = AirModalProps & {
  clips: Clip[];
};

export const BatchExportImagesModal = memo(({ clips, onClose }: BatchExportImagesModalProps) => {
  const formId = useId();
  const { exportImages } = useBatchExportImages();

  const { compatibleClips, skippedCount, overCapCount } = useMemo(() => {
    const compatible = clips.filter((clip) => isClipExportable(clip));
    const overCap = Math.max(0, compatible.length - MAX_BATCH_EXPORT_CLIPS);
    return {
      compatibleClips: compatible.slice(0, MAX_BATCH_EXPORT_CLIPS),
      skippedCount: clips.length - compatible.length,
      overCapCount: overCap,
    };
  }, [clips]);

  const batchClips = useMemo<BatchExportClip[]>(
    () =>
      compatibleClips
        .map((clip) => {
          const exportSource = getBestImgixExportSource(clip.ext, clip.assets);
          if (!exportSource) {
            return undefined;
          }

          return {
            id: clip.id,
            imageSrc: exportSource,
            fileName: clip.title || clip.importedName || clip.id,
            height: clip.height,
            width: clip.width,
          };
        })
        .filter((clip): clip is BatchExportClip => !!clip),
    [compatibleClips],
  );

  const { control, handleSubmit } = useForm<BatchExportImagesFormInput>({
    defaultValues: { format: 'jpg', scale: '1', suffix: '' },
    resolver: yupResolver(BatchExportImagesFormValidationSchema),
  });

  const onSubmit = useCallback(
    (values: BatchExportImagesFormInput) => {
      exportImages(batchClips, { ...values, suffix: values.suffix ?? '' });
      onClose();
    },
    [exportImages, batchClips, onClose],
  );

  const suffix = useWatch({ control, name: 'suffix' });
  const format = useWatch({ control, name: 'format' });

  const exportButtonLabel = useMemo(() => {
    const count = batchClips.length;
    if (count === 0) {
      return 'Export';
    }
    return `Export ${pluralize('asset', count, true)}`;
  }, [batchClips.length]);

  return (
    <Modal isOpen onDismiss={onClose}>
      <header className="mb-6 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <ModalTitle>Export {pluralize('asset', batchClips.length, true)} as</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </div>
        {skippedCount > 0 && (
          <Banner appearance="warning" size="small">
            {pluralize('asset', skippedCount, true)} in your selection {skippedCount === 1 ? 'is' : 'are'} not an
            exportable format and will be skipped.
          </Banner>
        )}
        {overCapCount > 0 && (
          <Banner appearance="info" size="small">
            Only the first {MAX_BATCH_EXPORT_CLIPS} assets will be exported.
          </Banner>
        )}
      </header>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel htmlFor={`${formId}-scale`}>Scale</FieldLabel>
            <Controller
              control={control}
              name="scale"
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full" id={`${formId}-scale`} />
                  <SelectContent>
                    {SCALE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${formId}-suffix`}>Suffix (optional)</FieldLabel>
            <Controller
              control={control}
              name="suffix"
              render={({ field }) => (
                <Input
                  {...field}
                  autoComplete="off"
                  className="w-full"
                  id={`${formId}-suffix`}
                  placeholder="e.g. _final"
                  value={field.value ?? ''}
                />
              )}
            />
            <div className="mt-2 break-all text-12 text-grey-10">{`Preview: {filename}${suffix}.${format}`}</div>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${formId}-format`}>Format</FieldLabel>
            <Controller
              control={control}
              name="format"
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full" id={`${formId}-format`} />
                  <SelectContent>
                    {FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </div>
      </form>
      <div className="mt-8 flex justify-end gap-3">
        <Button appearance="ghost" color="grey" onClick={onClose} size="large" type="button">
          Cancel
        </Button>
        <Button
          appearance="filled"
          color="blue"
          disabled={batchClips.length === 0}
          form={formId}
          size="large"
          type="submit"
        >
          {exportButtonLabel}
        </Button>
      </div>
    </Modal>
  );
});

BatchExportImagesModal.displayName = 'BatchExportImagesModal';
